import {
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import {
    manageUserService,
    UserListModel,
} from "@/services/api/manage-user/ManageUserService";

/**
 * Hook for fetching all users with React Query caching
 * Provides centralized user data management for dropdowns and lookups
 */
export function useGetAllUsers(options?: UseQueryOptions<UserListModel[]>) {
    return useQuery<UserListModel[]>({
        queryKey: ["users", "all"],
        queryFn: () => manageUserService.getAllUsers(),
        staleTime: 15 * 60 * 1000, // 15 minutes - users don't change frequently
        gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
        refetchOnMount: false, // Respect cache instead of always refetching
        refetchOnWindowFocus: false, // Don't refetch on window focus
        ...options,
    });
}

/**
 * Hook for prefetching user data
 * Used by prefetch hooks to warm the cache
 */
export function usePrefetchUsers() {
    const queryClient = useQueryClient();

    const prefetchUsers = async () => {
        try {
            await queryClient.prefetchQuery({
                queryKey: ["users", "all"],
                queryFn: () => manageUserService.getAllUsers(),
                staleTime: 15 * 60 * 1000,
            });
        } catch (error) {
            console.warn("Failed to prefetch users:", error);
        }
    };

    return { prefetchUsers };
}
